// components/artwork/modals/UseMyPhoneModal.jsx
// Passerelle desktop → mobile : QR contextualisé + retour realtime (« ✓ reçu »).
// Réutilisable pour tous les types de capture (facture, certificat, verso, signature,
// détail, vue principale, constat). Si `typeOptions` est fourni, on demande d'abord
// le type (comme le prévoit la spec), puis on génère la session.
import React, { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { useTranslation } from "react-i18next";
import { createCaptureSession, subscribeToSession } from "@/lib/qrBridge";

export default function UseMyPhoneModal({
  open, onClose, artworkId, actionType, documentType = null,
  typeOptions = null, onCompleted,
}) {
  const { t } = useTranslation();
  const [chosenType, setChosenType] = useState(documentType);
  const [qrData, setQrData] = useState("");
  const [status, setStatus] = useState("idle"); // idle | creating | pending | opened | completed | error
  const unsubRef = useRef(null);

  // Réinitialise à l'ouverture.
  useEffect(() => {
    if (open) { setChosenType(documentType); setQrData(""); setStatus("idle"); }
  }, [open, documentType]);

  // Doit-on d'abord demander le type ?
  const needsPick = !!typeOptions && !chosenType;

  useEffect(() => {
    if (!open || needsPick) return;
    let alive = true;
    setStatus("creating"); setQrData("");

    (async () => {
      try {
        const { sessionId, url } = await createCaptureSession({ artworkId, actionType, documentType: chosenType });
        if (!alive) return;
        const dataUrl = await QRCode.toDataURL(url, { margin: 1, width: 240, color: { dark: "#1a1a1a", light: "#ffffff" } });
        if (!alive) return;
        setQrData(dataUrl);
        setStatus("pending");
        unsubRef.current = subscribeToSession(sessionId, (row) => {
          if (row.status === "opened") setStatus("opened");
          if (row.status === "completed") { setStatus("completed"); onCompleted?.(row); }
        });
      } catch { if (alive) setStatus("error"); }
    })();

    return () => {
      alive = false;
      if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
    };
  }, [open, needsPick, chosenType, artworkId, actionType, onCompleted]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div
        className="w-full max-w-sm rounded-2xl shadow-xl p-6 text-center"
        style={{ fontFamily: "Georgia, 'Times New Roman', serif", background: "#fff" }}
        onClick={(e) => e.stopPropagation()}
      >
        {needsPick ? (
          <>
            <h3 className="text-xl font-bold mb-1" style={{ color: "#1a1a1a" }}>{t("usePhone.title")}</h3>
            <p className="text-sm mb-5" style={{ color: "#6b6459" }}>{t("usePhone.chooseType")}</p>
            <div className="grid gap-2">
              {typeOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setChosenType(opt.value)}
                  className="reg-card w-full py-3 rounded-lg text-sm font-medium"
                  style={{ color: "#1a1a1a" }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button onClick={onClose} className="mt-5 w-full py-2.5 text-sm" style={{ color: "#6b6459" }}>
              {t("usePhone.cancel")}
            </button>
          </>
        ) : status === "completed" ? (
          <>
            <div className="w-16 h-16 rounded-full text-3xl flex items-center justify-center mx-auto mb-4" style={{ background: "#e9f3ec", color: "#3b7d4f" }} aria-hidden="true">✓</div>
            <h3 className="text-xl font-bold mb-2" style={{ color: "#1a1a1a" }}>{t("usePhone.received.title")}</h3>
            <p className="mb-6" style={{ color: "#6b6459" }}>{t("usePhone.received.body")}</p>
            <button onClick={onClose} className="reg-btn-gold w-full py-3 rounded-lg font-semibold">{t("usePhone.close")}</button>
          </>
        ) : (
          <>
            <h3 className="text-xl font-bold mb-1" style={{ color: "#1a1a1a" }}>{t("usePhone.title")}</h3>
            <p className="text-sm mb-5" style={{ color: "#6b6459" }}>{t("usePhone.subtitle")}</p>

            <div className="mx-auto w-[240px] h-[240px] rounded-xl reg-card flex items-center justify-center overflow-hidden">
              {qrData ? (
                <img src={qrData} alt={t("usePhone.qrAlt")} width={240} height={240} />
              ) : (
                <div className="w-10 h-10 rounded-full animate-spin" style={{ border: "2px solid #e5e0d8", borderTopColor: "#c9a961" }} />
              )}
            </div>

            <div className="mt-5 flex items-center justify-center gap-2 text-sm">
              {status === "opened" ? (
                <>
                  <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#c9a961" }} />
                  <span className="font-medium" style={{ color: "#8a6d1f" }}>{t("usePhone.connected")}</span>
                </>
              ) : status === "error" ? (
                <span style={{ color: "#b3261e" }}>{t("usePhone.error")}</span>
              ) : (
                <span style={{ color: "#8a8378" }}>{t("usePhone.waiting")}</span>
              )}
            </div>

            <button onClick={onClose} className="reg-card mt-6 w-full py-3 rounded-lg" style={{ color: "#6b6459" }}>
              {t("usePhone.cancel")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
